import { AlertCircle, XClose } from '@untitledui/icons';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import { cx } from '@/utils/cx';

import { PROPOSAL_TOAST_POSITION } from './toastPosition';

const FAILURE_TOAST_OPTIONS = {
  position: PROPOSAL_TOAST_POSITION,
  // Override Sonner's default wrapper styles so only the inner card renders
  // (prevents double-border from the Toaster's default border/padding/shadow).
  style: {
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    padding: 0,
  },
};

interface ShowExecutionFailureToastParams {
  orgId: number;
  proposalId: number;
  message: string;
  proposalTitle?: string;
}

/**
 * Show a persistent error toast for execution/partial-failure/approve-request
 * failures and tracker timeouts. Does not auto-dismiss — the "View" action and
 * the dismiss control are the only way to close it, since it may be the sole
 * way back to a proposal the user has already navigated away from.
 */
export function showExecutionFailureToast({
  orgId,
  proposalId,
  message,
  proposalTitle,
}: ShowExecutionFailureToastParams): void {
  toast.custom(
    t => (
      <div
        className={cx(
          'flex items-start gap-3 p-4 bg-utility-error-50 border border-utility-error-200 rounded-xl shadow-lg min-w-[320px] max-w-[420px]'
        )}
      >
        <div className='flex items-center justify-center size-9 rounded-full bg-utility-error-100 shrink-0 mt-0.5'>
          <AlertCircle
            className='size-5 text-utility-error-700'
            aria-hidden='true'
          />
        </div>

        <div className='flex-1 min-w-0 space-y-2'>
          <div className='min-w-0'>
            <p className='text-sm font-semibold text-utility-error-700'>
              {message}
            </p>
            {proposalTitle && (
              <p className='text-xs text-utility-error-700/80 mt-0.5 truncate'>
                {proposalTitle}
              </p>
            )}
          </div>

          <Button
            size='sm'
            color='secondary'
            onClick={() => {
              window.location.assign(`/${orgId}/actions/${proposalId}`);
              toast.dismiss(t);
            }}
          >
            View
          </Button>
        </div>

        <button
          type='button'
          onClick={() => toast.dismiss(t)}
          className='text-tertiary hover:text-fg-secondary transition-colors p-1 -m-1 rounded-md hover:bg-tertiary shrink-0'
          aria-label='Dismiss'
        >
          <XClose className='size-4' />
        </button>
      </div>
    ),
    { ...FAILURE_TOAST_OPTIONS, duration: Infinity }
  );
}
